import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Button, StatusBadge, Modal, ModalContent } from '../common';
import { Heading, Text, Typography } from '../common/Typography';
import { Card } from '../common/Card';
import { getGenderTextColor } from '../../utils/genderColors';
import { formatPlayerName } from '../../utils/nameUtils';
import { formatTournamentEntryLabel } from '../../utils/tournamentFormat';

const COURT_ASSIGN_HELP =
  'Pin this group’s matches to a numbered court, or leave Auto (any court) to spread play across courts.';

/**
 * EditGroupsSection Component
 * Drag-and-drop group assignments (hosts).
 */
const EditGroupsSection = ({
  tournament,
  teams = [],
  groups = [],
  onSaveChanges,
  onCancel,
  numberOfCourts = 1,
  onAssignCourtToGroup,
  heading = 'Group setup',
  description = 'Use Add team in each group for the quickest setup on phones. On desktop you can also drag players between groups. Use Auto-assign / Randomise in the toolbar, then Save when each group has at least two teams.'
}) => {
  /** Mobile / touch: HTML5 DnD is unreliable; use tap team → tap group instead */
  const [tapToMove, setTapToMove] = useState(false);
  useEffect(() => {
    const update = () => {
      const coarse = window.matchMedia('(pointer: coarse)').matches;
      const noHover = window.matchMedia('(hover: none)').matches;
      const hasTouch = typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0;
      setTapToMove(coarse || (hasTouch && noHover));
    };
    update();
    const m1 = window.matchMedia('(pointer: coarse)');
    const m2 = window.matchMedia('(hover: none)');
    m1.addEventListener('change', update);
    m2.addEventListener('change', update);
    return () => {
      m1.removeEventListener('change', update);
      m2.removeEventListener('change', update);
    };
  }, []);

  /** Collapsible unassigned — start collapsed on touch UIs to leave room for groups + Add team */
  const [unassignedExpanded, setUnassignedExpanded] = useState(() => {
    if (typeof window === 'undefined') return true;
    const coarse = window.matchMedia('(pointer: coarse)').matches;
    const noHover = window.matchMedia('(hover: none)').matches;
    const hasTouch = navigator.maxTouchPoints > 0;
    return !(coarse || (hasTouch && noHover));
  });

  const [addTeamForGroupId, setAddTeamForGroupId] = useState(null);

  const [groupAssignments, setGroupAssignments] = useState(() => {
    // Initialize from existing group assignments
    const assignments = {};
    groups.forEach(group => {
      group.tournament_group_entries?.forEach(gt => {
        assignments[gt.entry_id] = group.id;
      });
    });
    return assignments;
  });
  const [draggedTeam, setDraggedTeam] = useState(null);
  /** Selected team for tap-to-move (touch / coarse pointer) */
  const [pickedTeam, setPickedTeam] = useState(null);
  const [saving, setSaving] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  // Get teams by group
  const teamsByGroup = useMemo(() => {
    const byGroup = {};
    groups.forEach(group => {
      byGroup[group.id] = teams.filter(team => groupAssignments[team.id] === group.id);
    });
    return byGroup;
  }, [teams, groups, groupAssignments]);

  // Get unassigned teams
  const unassignedTeams = useMemo(() => {
    return teams.filter(team => !groupAssignments[team.id]);
  }, [teams, groupAssignments]);

  const addTeamModalGroup = useMemo(
    () => groups.find((g) => g.id === addTeamForGroupId) || null,
    [groups, addTeamForGroupId]
  );

  const pickerTeams = useMemo(() => {
    if (!addTeamForGroupId) return [];
    return teams
      .filter((t) => groupAssignments[t.id] !== addTeamForGroupId)
      .map((t) => {
        const gid = groupAssignments[t.id];
        const fromLabel = gid
          ? `In Group ${groups.find((g) => g.id === gid)?.name ?? ''}`
          : 'Unassigned';
        return { team: t, fromLabel };
      })
      .sort((a, b) => {
        const au = !groupAssignments[a.team.id];
        const bu = !groupAssignments[b.team.id];
        if (au !== bu) return au ? -1 : 1;
        return a.fromLabel.localeCompare(b.fromLabel);
      });
  }, [addTeamForGroupId, teams, groupAssignments, groups]);

  const openAddTeamModal = useCallback((groupId) => {
    setPickedTeam(null);
    setAddTeamForGroupId(groupId);
  }, []);

  const handleAssignTeam = (teamId, groupId) => {
    setGroupAssignments(prev => ({
      ...prev,
      [teamId]: groupId
    }));
  };

  const handleUnassignTeam = (teamId) => {
    setGroupAssignments(prev => {
      const newAssignments = { ...prev };
      delete newAssignments[teamId];
      return newAssignments;
    });
  };

  const handleDragStart = (team) => {
    setDraggedTeam(team);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (groupId) => {
    if (draggedTeam) {
      handleAssignTeam(draggedTeam.id, groupId);
      setDraggedTeam(null);
    }
  };

  const handleDropToUnassigned = () => {
    if (draggedTeam) {
      handleUnassignTeam(draggedTeam.id);
      setDraggedTeam(null);
    }
  };

  const togglePickTeam = (team) => {
    setPickedTeam((prev) => (prev?.id === team.id ? null : team));
  };

  const handleTapAssignToGroup = (groupId) => {
    if (!pickedTeam || !tapToMove) return;
    handleAssignTeam(pickedTeam.id, groupId);
    setPickedTeam(null);
  };

  const handleTapUnassignPicked = () => {
    if (!pickedTeam || !tapToMove) return;
    handleUnassignTeam(pickedTeam.id);
    setPickedTeam(null);
  };

  const handleSave = async () => {
    // Validate minimum teams per group (at least 2)
    const invalidGroups = groups.filter(group => {
      const teamCount = teamsByGroup[group.id]?.length || 0;
      return teamCount < 2;
    });
    
    if (invalidGroups.length > 0) {
      alert(`Each group must have at least 2 teams. Group(s) ${invalidGroups.map(g => g.name).join(', ')} need more teams.`);
      return;
    }

    setSaving(true);
    try {
      // Convert to the format expected by the service
      // From: { teamId: groupId } to { groupId: [teamIds] }
      const assignments = {};
      groups.forEach(group => {
        assignments[group.id] = Object.keys(groupAssignments)
          .filter(teamId => groupAssignments[teamId] === group.id);
      });
      
      await onSaveChanges(assignments, unassignedTeams);
    } catch (err) {
      console.error('Error saving changes:', err);
      alert('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = useMemo(() => {
    // Check if assignments have changed from original
    const originalAssignments = {};
    groups.forEach(group => {
      group.tournament_group_entries?.forEach(gt => {
        originalAssignments[gt.entry_id] = group.id;
      });
    });
    
    return JSON.stringify(groupAssignments) !== JSON.stringify(originalAssignments);
  }, [groups, groupAssignments]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <Heading level={3} className="mb-2">{heading}</Heading>
          <Text className="text-text-secondary text-sm">
            {description}
            {tapToMove && (
              <span className="mt-2 block rounded-md bg-primary/10 px-2 py-1.5 text-text-primary dark:bg-primary/20">
                <strong>On this device:</strong> use <strong>Add team</strong> inside each group for the fastest flow.
                You can also tap a team to select it, then tap inside a group to place them (or use <strong>Unassign</strong>).
              </span>
            )}
          </Text>
        </div>
        <div className="flex shrink-0 flex-wrap justify-end gap-2">
          {typeof onCancel === 'function' && (
            <Button variant="secondary" size="small" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button
            variant="success"
            size="small"
            onClick={handleSave}
            disabled={saving || !hasChanges}
          >
            {saving ? 'Saving...' : 'Save changes'}
          </Button>
        </div>
      </div>

      <details
        className="rounded-lg border border-border-subtle bg-surface-subtle/40 dark:border-white/[0.08]"
        onToggle={(e) => setHelpOpen(e.currentTarget.open)}
      >
        <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2.5 text-sm font-medium text-primary hover:text-primary-dark sm:px-4 [&::-webkit-details-marker]:hidden">
          <svg
            className={`h-4 w-4 shrink-0 text-text-muted transition-transform duration-200 ${helpOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
          How this works
        </summary>
        <div className="border-t border-border-subtle px-3 pb-3 pt-2 sm:px-4">
          <Typography variant="body" className="text-sm text-text-secondary">
            <ul className="ml-5 list-disc space-y-1">
              <li>
                <strong>Phones / tablets:</strong> tap <strong>Add team</strong> in a group and pick from the list (fastest)
              </li>
              <li>Drag teams between groups on desktop (mouse / trackpad)</li>
              <li>On touch devices you can also tap a team, then tap a group&apos;s player area to move them</li>
              <li>Expand <strong>Unassigned teams</strong> if you need to drag from the pool or tap-select from there</li>
              <li>Drag to the unassigned pool to remove from groups (or tap team then Unassign)</li>
              <li>Tap &quot;Remove&quot; on a team to unassign it</li>
              <li>Each group must have at least 2 teams</li>
              <li>Saving applies assignments and refreshes group-stage matches when needed</li>
            </ul>
          </Typography>
        </div>
      </details>

      {/* Warning about match regeneration */}
      {tournament.status !== 'draft' && (
        <Card variant="warning" size="small">
          <Typography variant="body" className="text-sm">
            <strong>⚠️ Warning:</strong> Changing group assignments will regenerate all matches for affected groups. 
            Existing match results will be preserved where possible.
          </Typography>
        </Card>
      )}

      {tapToMove && pickedTeam && (
        <Card variant="default" size="small" className="sticky top-2 z-[5] border-2 border-primary/40 shadow-md">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <Typography variant="body" className="text-sm min-w-0">
              <span className="text-text-secondary">Moving: </span>
              <strong className="text-text-primary">
                {pickedTeam.player2 ? (
                  <>
                    {formatPlayerName(pickedTeam.player1)} &amp; {formatPlayerName(pickedTeam.player2)}
                  </>
                ) : (
                  formatTournamentEntryLabel(pickedTeam, true)
                )}
              </strong>
              <span className="text-text-secondary"> — tap a group box below.</span>
            </Typography>
            <div className="flex flex-wrap gap-2 shrink-0">
              <Button type="button" variant="warning" size="small" onClick={handleTapUnassignPicked}>
                Unassign
              </Button>
              <Button type="button" variant="ghost" size="small" onClick={() => setPickedTeam(null)}>
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Unassigned Teams Pool */}
      {unassignedTeams.length > 0 && (
        <Card variant="warning" size="medium">
          <button
            type="button"
            className="mb-3 flex w-full touch-manipulation items-center gap-2 rounded-lg px-1 py-2 text-left -mx-1 hover:bg-black/[0.04] dark:hover:bg-white/[0.06]"
            onClick={() => setUnassignedExpanded((v) => !v)}
            aria-expanded={unassignedExpanded}
          >
            <svg
              className={`h-5 w-5 shrink-0 text-text-muted transition-transform duration-200 ${unassignedExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
            <Heading level={4} className="mb-0 min-w-0 flex-1">
              Unassigned teams ({unassignedTeams.length})
            </Heading>
            <span className="sr-only">{unassignedExpanded ? 'Collapse' : 'Expand'}</span>
          </button>
          {unassignedExpanded && (
          <div 
            className="grid grid-cols-1 md:grid-cols-2 gap-2"
            onDragOver={handleDragOver}
            onDrop={handleDropToUnassigned}
          >
            {unassignedTeams.map(team => (
              <div
                key={team.id}
                draggable={!tapToMove}
                onDragStart={() => handleDragStart(team)}
                onClick={() => tapToMove && togglePickTeam(team)}
                onKeyDown={(e) => {
                  if (!tapToMove) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    togglePickTeam(team);
                  }
                }}
                role={tapToMove ? 'button' : undefined}
                tabIndex={tapToMove ? 0 : undefined}
                className={`p-3 bg-surface border-2 border-dashed border-warning rounded-lg transition-colors touch-manipulation ${
                  tapToMove
                    ? pickedTeam?.id === team.id
                      ? 'cursor-pointer ring-2 ring-primary ring-offset-2 ring-offset-background bg-primary/5'
                      : 'cursor-pointer active:bg-surface-subtle hover:bg-surface-subtle'
                    : 'cursor-move hover:bg-surface-subtle'
                }`}
              >
                <Typography variant="body" className="text-sm">
                  {team.player2 ? (
                    <>
                      <span className={getGenderTextColor(team.player1?.gender || '')}>
                        {formatPlayerName(team.player1)}
                      </span>
                      {' & '}
                      <span className={getGenderTextColor(team.player2?.gender || '')}>
                        {formatPlayerName(team.player2)}
                      </span>
                    </>
                  ) : (
                    <span className={getGenderTextColor(team.player1?.gender || '')}>
                      {formatTournamentEntryLabel(team, true)}
                    </span>
                  )}
                </Typography>
              </div>
            ))}
          </div>
          )}
        </Card>
      )}

      {/* Groups */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {groups.map(group => {
          const groupTeams = teamsByGroup[group.id] || [];
          
          return (
            <Card
              key={group.id}
              variant="muted"
              size="medium"
              onDragOver={handleDragOver}
              onDrop={() => handleDrop(group.id)}
            >
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-2">
                    <Heading level={4} className="mb-0">
                      Group {group.name}
                    </Heading>
                    <div className="flex flex-wrap items-center gap-2">
                      <Text className="text-text-secondary text-sm">
                        {groupTeams.length} team{groupTeams.length !== 1 ? 's' : ''}
                      </Text>
                      {groupTeams.length < 2 && (
                        <StatusBadge variant="danger" size="small">
                          Need 2+ teams
                        </StatusBadge>
                      )}
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="primary"
                    size="small"
                    className="w-full shrink-0 touch-manipulation sm:w-auto"
                    onClick={() => openAddTeamModal(group.id)}
                  >
                    Add team
                  </Button>
                </div>
                {typeof onAssignCourtToGroup === 'function' && numberOfCourts > 1 && (
                  <div className="flex w-full flex-col gap-1 sm:w-auto sm:items-end sm:shrink-0">
                    <label
                      htmlFor={`edit-section-group-${group.id}-court`}
                      className="text-xs font-semibold uppercase tracking-wide text-text-secondary"
                      title={COURT_ASSIGN_HELP}
                    >
                      Court for this group
                    </label>
                    <select
                      id={`edit-section-group-${group.id}-court`}
                      value={group.assigned_court ?? ''}
                      onChange={(e) =>
                        onAssignCourtToGroup(
                          group.id,
                          e.target.value ? parseInt(e.target.value, 10) : null
                        )
                      }
                      className="min-w-[10rem] rounded-md border border-border-medium bg-surface px-2 py-1.5 text-sm text-text-primary focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="">Auto (any court)</option>
                      {Array.from({ length: numberOfCourts }, (_, i) => (
                        <option key={i + 1} value={i + 1}>
                          Court {i + 1}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Drop zone — whole area is tap target on mobile when a team is picked */}
              <div
                className={`min-h-[100px] space-y-2 rounded-lg transition-colors ${
                  tapToMove && pickedTeam
                    ? 'cursor-pointer bg-primary/[0.04] ring-1 ring-dashed ring-primary/40 p-1 -m-1'
                    : ''
                }`}
                onClick={() => handleTapAssignToGroup(group.id)}
                onKeyDown={(e) => {
                  if (!tapToMove || !pickedTeam) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleTapAssignToGroup(group.id);
                  }
                }}
                role={tapToMove && pickedTeam ? 'button' : undefined}
                tabIndex={tapToMove && pickedTeam ? 0 : undefined}
              >
                {groupTeams.length === 0 ? (
                  <div className="h-24 border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center gap-1 px-2">
                    <Text className="text-text-muted text-sm text-center">
                      {tapToMove ? 'Tap here after choosing a team' : 'Drop teams here'}
                    </Text>
                  </div>
                ) : (
                  groupTeams.map(team => (
                    <div
                      key={team.id}
                      draggable={!tapToMove}
                      onDragStart={() => handleDragStart(team)}
                      onClick={(e) => {
                        if (!tapToMove) return;
                        e.stopPropagation();
                        if (pickedTeam) {
                          if (pickedTeam.id === team.id) {
                            setPickedTeam(null);
                          } else {
                            handleAssignTeam(pickedTeam.id, group.id);
                            setPickedTeam(null);
                          }
                        } else {
                          togglePickTeam(team);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (!tapToMove) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          e.stopPropagation();
                          if (pickedTeam) {
                            if (pickedTeam.id === team.id) {
                              setPickedTeam(null);
                            } else {
                              handleAssignTeam(pickedTeam.id, group.id);
                              setPickedTeam(null);
                            }
                          } else {
                            togglePickTeam(team);
                          }
                        }
                      }}
                      role={tapToMove ? 'button' : undefined}
                      tabIndex={tapToMove ? 0 : undefined}
                      className={`p-3 bg-surface border border-border-medium rounded-lg transition-colors group touch-manipulation ${
                        tapToMove
                          ? pickedTeam?.id === team.id
                            ? 'cursor-pointer ring-2 ring-primary ring-offset-2 ring-offset-background'
                            : 'cursor-pointer active:bg-surface-subtle'
                          : 'cursor-move hover:bg-surface-subtle'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <Typography variant="body" className="text-sm">
                          {team.player2 ? (
                            <>
                              <span className={getGenderTextColor(team.player1?.gender || '')}>
                                {formatPlayerName(team.player1)}
                              </span>
                              {' & '}
                              <span className={getGenderTextColor(team.player2?.gender || '')}>
                                {formatPlayerName(team.player2)}
                              </span>
                            </>
                          ) : (
                            <span className={getGenderTextColor(team.player1?.gender || '')}>
                              {formatTournamentEntryLabel(team, true)}
                            </span>
                          )}
                        </Typography>
                        <Button
                          type="button"
                          variant="secondary"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (pickedTeam?.id === team.id) setPickedTeam(null);
                            handleUnassignTeam(team.id);
                          }}
                          className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Changes indicator */}
      {hasChanges && (
        <Card variant="info" size="small">
          <Typography variant="body" className="text-sm">
            ✏️ You have unsaved changes. Click &quot;Save changes&quot; to apply them.
          </Typography>
        </Card>
      )}

      <Modal
        isOpen={!!addTeamForGroupId && !!addTeamModalGroup}
        onClose={() => setAddTeamForGroupId(null)}
      >
        <ModalContent size="medium" padding={false} className="flex min-h-0 flex-col overflow-hidden p-4 sm:p-6">
          <Heading level={4} id="edit-groups-add-team-title" className="mb-2 shrink-0">
            Add to Group {addTeamModalGroup?.name}
          </Heading>
          <Text className="mb-3 shrink-0 text-sm text-text-secondary">
            Tap a team or pair to move them into this group. You can add several in a row; close when you&apos;re done.
          </Text>
          <div
            className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto overscroll-contain pb-2"
            role="list"
            aria-labelledby="edit-groups-add-team-title"
          >
            {pickerTeams.length === 0 ? (
              <Text className="text-sm text-text-muted">
                No one available to add — everyone is already in this group.
              </Text>
            ) : (
              pickerTeams.map(({ team, fromLabel }) => (
                <button
                  key={team.id}
                  type="button"
                  className="flex w-full touch-manipulation flex-col gap-0.5 rounded-lg border border-border-medium bg-surface p-3 text-left transition-colors active:bg-surface-subtle hover:bg-surface-subtle"
                  onClick={() => handleAssignTeam(team.id, addTeamForGroupId)}
                >
                  <Typography variant="body" className="text-sm text-text-primary">
                    {team.player2 ? (
                      <>
                        <span className={getGenderTextColor(team.player1?.gender || '')}>
                          {formatPlayerName(team.player1)}
                        </span>
                        {' & '}
                        <span className={getGenderTextColor(team.player2?.gender || '')}>
                          {formatPlayerName(team.player2)}
                        </span>
                      </>
                    ) : (
                      <span className={getGenderTextColor(team.player1?.gender || '')}>
                        {formatTournamentEntryLabel(team, true)}
                      </span>
                    )}
                  </Typography>
                  <span className="text-xs text-text-muted">{fromLabel}</span>
                </button>
              ))
            )}
          </div>
          <div className="mt-4 flex shrink-0 justify-end gap-2 border-t border-border-subtle pt-3 sm:border-0 sm:pt-0">
            <Button type="button" variant="secondary" size="small" className="w-full sm:w-auto" onClick={() => setAddTeamForGroupId(null)}>
              Done
            </Button>
          </div>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default EditGroupsSection;
